// src/utils/planUtils.js
// Utilities for plan/quota management so response limits stay consistent across the app.

import { db } from '../firebase'
import { doc, getDoc, runTransaction, Timestamp, updateDoc } from 'firebase/firestore'

export const DEFAULT_PLAN_NAME = 'Free'
export const DEFAULT_RESPONSE_LIMIT = 50
const RESET_PERIOD_DAYS = 30

const toDateSafe = (value) => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value.toDate === 'function') return value.toDate()
  return null
}

export const getNextResetDate = (fromDate = new Date()) => {
  const next = new Date(fromDate)
  next.setDate(next.getDate() + RESET_PERIOD_DAYS)
  return next
}

export const normalizePlanData = (raw = {}) => {
  const responseLimit = raw.responseLimit ?? DEFAULT_RESPONSE_LIMIT
  const responsesUsed = raw.responsesUsed ?? 0
  const responseResetAt =
    toDateSafe(raw.responseResetAt) ||
    getNextResetDate()

  return {
    ...raw,
    planName: raw.planName || DEFAULT_PLAN_NAME,
    responseLimit,
    responsesUsed,
    responseResetAt
  }
}

export async function ensureOrganizationPlanDefaults(organizationId) {
  if (!organizationId) return null
  const orgRef = doc(db, 'organizations', organizationId)
  const snap = await getDoc(orgRef)
  if (!snap.exists()) return null

  const data = snap.data() || {}
  const updates = {}

  if (data.responseLimit == null) updates.responseLimit = DEFAULT_RESPONSE_LIMIT
  if (data.responsesUsed == null) updates.responsesUsed = 0
  if (!data.responseResetAt) updates.responseResetAt = Timestamp.fromDate(getNextResetDate())
  if (!data.planName) updates.planName = DEFAULT_PLAN_NAME

  if (Object.keys(updates).length) {
    await updateDoc(orgRef, updates)
  }

  return { id: organizationId, ...data, ...updates }
}

export async function consumeResponseQuota(organizationId) {
  if (!organizationId) return null

  const orgRef = doc(db, 'organizations', organizationId)

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(orgRef)
    if (!snap.exists()) {
      throw new Error('Organization not found')
    }

    const data = snap.data() || {}
    const now = new Date()
    const responseLimit = data.responseLimit ?? DEFAULT_RESPONSE_LIMIT
    const planName = data.planName || DEFAULT_PLAN_NAME

    let responseResetAt = toDateSafe(data.responseResetAt)
    let responsesUsed = data.responsesUsed ?? 0

    if (!responseResetAt || now > responseResetAt) {
      responseResetAt = getNextResetDate(now)
      responsesUsed = 0
    }

    const newUsed = Math.min(responsesUsed + 1, responseLimit)

    transaction.update(orgRef, {
      responsesUsed: newUsed,
      responseLimit,
      planName,
      responseResetAt: Timestamp.fromDate(responseResetAt)
    })

    return {
      planName,
      responseLimit,
      responsesUsed: newUsed,
      responseResetAt
    }
  })
}
